import React, { useEffect } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MiraChatLink from 'components/mira-chat-link';
import { miraUrlTransform } from 'utils/miraNavLinks';
import styles from '@/styles/features/loan-information/chat-model/WordByWord.module.css';

/**
 * Renders an AI chat reply as markdown.
 *
 * The reply arrives complete in one buffered frame, so it is rendered in full
 * at once (the legacy word-by-word reveal has been dropped). remark-gfm adds
 * tables, strikethrough, task lists and autolinks on top of CommonMark.
 */
// Safety net — the text handed in has already had its speech sections removed.
// A wrapped marker (__SPEECH__ / **SPEECH**) is unambiguous, so case is ignored
// there. Unwrapped, only ALL-CAPS SPEECH counts: matching a lowercase "speech"
// would truncate the visible answer at the ordinary English word.
const stripSpeech = (msg) => {
  if (!msg) return '';
  return msg
    .replace(/\n*(?:[_*]{1,2}[Ss][Pp][Ee][Ee][Cc][Hh][_*]{1,2}|SPEECH)(?::)?\s+[\s\S]+$/, '')
    .trim();
};

const CustomLink = ({ href, children }) => (
  <MiraChatLink href={href} className={styles.link}>
    {children}
  </MiraChatLink>
);

// Wrap tables so wide content scrolls horizontally inside the chat panel.
const CustomTable = ({ children }) => (
  <div style={{ maxWidth: '100%', overflowX: 'auto', margin: '10px 0' }}>
    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px', border: '1px solid #d5d9e0' }}>
      {children}
    </table>
  </div>
);

const REMARK_PLUGINS = [remarkGfm];

// `signalKey` decides when the "reply delivered" effect fires: pass the
// message's original-language content so a cosmetic text swap (the language
// toggle) doesn't reset the global typing state while another query is in
// flight. Falls back to the rendered text when not provided.
const WordByWord = ({ text, setIsTypeWriterActive, setTyping, signalKey }) => {
  const cleanText = stripSpeech(text);
  const effectKey = signalKey !== undefined ? signalKey : cleanText;

  useEffect(() => {
    if (typeof setTyping === 'function') setTyping();
    if (typeof setIsTypeWriterActive === 'function') {
      setIsTypeWriterActive((n) => (typeof n === 'number' ? n + 1 : 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectKey]);

  return (
    <div className={styles.container}>
      <Markdown
        className={styles.markdown}
        remarkPlugins={REMARK_PLUGINS}
        urlTransform={miraUrlTransform}
        components={{
          a: CustomLink,
          table: CustomTable,
          th: ({ children }) => (
            <th style={{ border: '1px solid #e2e6ee', padding: '7px 10px', textAlign: 'left', background: '#f1f4f9', fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</th>
          ),
          td: ({ children }) => (
            <td style={{ border: '1px solid #e2e6ee', padding: '7px 10px', textAlign: 'left', verticalAlign: 'top' }}>{children}</td>
          ),
          h3: ({ children }) => <h3 className={styles.heading3}>{children}</h3>,
          h4: ({ children }) => <h4 className={styles.heading4}>{children}</h4>,
          strong: ({ children }) => <strong className={styles.strong}>{children}</strong>,
          p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
          ul: ({ children }) => <ul className={styles.list}>{children}</ul>,
          ol: ({ node, children, ...props }) => (
            <ol className={styles.orderedList} {...props}>{children}</ol>
          ),
          li: ({ children }) => <li className={styles.listItem}>{children}</li>,
          pre: ({ children }) => <pre className={styles.codeBlock}>{children}</pre>,
          code: ({ children }) => <code className={styles.inlineCode}>{children}</code>,
        }}
      >
        {cleanText || ''}
      </Markdown>
    </div>
  );
};

export default WordByWord;
